import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog.jsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  GoogleLogo, Check, MagnifyingGlass, LinkBreak, Folder, VideoCamera,
  Image as ImageIcon, ArrowLeft, CaretRight, Play,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";

const ROOT_FOLDER = { id: "root", name: "My Drive" };

export default function GoogleDriveImport({ open, onOpenChange, onImported }) {
  const api = useApi();
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [selected, setSelected] = useState([]);
  const [importing, setImporting] = useState(false);
  const [thumbUrls, setThumbUrls] = useState({});

  // Browse state
  const [folderStack, setFolderStack] = useState([ROOT_FOLDER]);
  const [browseItems, setBrowseItems] = useState({ folders: [], files: [] });
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseNextPageToken, setBrowseNextPageToken] = useState(null);

  // Filename search state (kept alongside browsing)
  const [searchActive, setSearchActive] = useState(false);
  const [search, setSearch] = useState("");
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [nextPageToken, setNextPageToken] = useState(null);

  const visibleFiles = searchActive ? files : browseItems.files;

  useEffect(() => {
    if (open) {
      setSelected([]);
      setSearch("");
      setSearchActive(false);
      setFiles([]);
      setNextPageToken(null);
      setFolderStack([ROOT_FOLDER]);
      setBrowseItems({ folders: [], files: [] });
      setBrowseNextPageToken(null);
      fetchStatus();
    } else {
      Object.values(thumbUrls).forEach((u) => URL.revokeObjectURL(u));
      setThumbUrls({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Thumbnails need a Bearer token, which a plain <img src> can never send — fetch each as an
  // authenticated blob instead and render via a local object URL.
  useEffect(() => {
    const toFetch = visibleFiles.filter((f) => !thumbUrls[f.id]);
    if (toFetch.length === 0) return;
    let cancelled = false;
    (async () => {
      const results = await Promise.all(toFetch.map(async (f) => {
        try {
          const r = await api.get(f.thumbnail_url, { responseType: "blob" });
          return [f.id, URL.createObjectURL(r.data)];
        } catch (e) {
          return [f.id, null];
        }
      }));
      if (!cancelled) {
        setThumbUrls((prev) => {
          const next = { ...prev };
          results.forEach(([id, url]) => { if (url) next[id] = url; });
          return next;
        });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleFiles]);

  const fetchStatus = async () => {
    setLoadingStatus(true);
    try {
      const r = await api.get("/drive/status");
      setStatus(r.data);
      if (r.data?.connected) fetchBrowse("root");
    } catch (e) {
      toast.error("Couldn't check Google Drive connection");
    } finally {
      setLoadingStatus(false);
    }
  };

  const fetchBrowse = async (folderId, pageToken) => {
    setBrowseLoading(true);
    try {
      const params = { folder_id: folderId };
      if (pageToken) params.page_token = pageToken;
      const r = await api.get("/drive/browse", { params });
      setBrowseItems((prev) => ({
        folders: pageToken ? [...prev.folders, ...r.data.folders] : r.data.folders,
        files: pageToken ? [...prev.files, ...r.data.files] : r.data.files,
      }));
      setBrowseNextPageToken(r.data.next_page_token || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't browse that folder");
    } finally {
      setBrowseLoading(false);
    }
  };

  const openFolder = (folder) => {
    setFolderStack((prev) => [...prev, { id: folder.id, name: folder.name }]);
    fetchBrowse(folder.id);
  };

  const goToBreadcrumb = (index) => {
    setFolderStack((prev) => prev.slice(0, index + 1));
    fetchBrowse(folderStack[index].id);
  };

  const goBack = () => {
    if (folderStack.length <= 1) return;
    const newStack = folderStack.slice(0, -1);
    setFolderStack(newStack);
    fetchBrowse(newStack[newStack.length - 1].id);
  };

  const fetchFiles = async (pageToken, q) => {
    setFilesLoading(true);
    try {
      const params = {};
      if (pageToken) params.page_token = pageToken;
      if (q) params.q = q;
      const r = await api.get("/drive/files", { params });
      setFiles((prev) => (pageToken ? [...prev, ...r.data.files] : r.data.files));
      setNextPageToken(r.data.next_page_token || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load Drive files");
    } finally {
      setFilesLoading(false);
    }
  };

  const handleSearch = () => {
    const q = search.trim();
    if (!q) { toast.error("Type something to search"); return; }
    setSearchActive(true);
    setFiles([]);
    setNextPageToken(null);
    fetchFiles(null, q);
  };

  const clearSearch = () => {
    setSearch("");
    setSearchActive(false);
    setFiles([]);
    setNextPageToken(null);
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const r = await api.get("/drive/connect");
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start Google Drive connection");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.post("/drive/disconnect");
      toast.success("Google Drive disconnected");
      setStatus({ connected: false });
      setFiles([]);
      setBrowseItems({ folders: [], files: [] });
      setFolderStack([ROOT_FOLDER]);
    } catch (e) {
      toast.error("Couldn't disconnect");
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleImport = async () => {
    if (selected.length === 0) { toast.error("Select at least one file"); return; }
    setImporting(true);
    try {
      const r = await api.post("/drive/import", { file_ids: selected });
      const { imported, errors } = r.data;
      if (imported > 0) toast.success(`Imported ${imported} file(s) — pending review before use`);
      if (errors && errors.length) errors.forEach((e) => toast.error(`${e.name || e.file_id}: ${e.error}`));
      setSelected([]);
      onOpenChange(false);
      onImported && onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const renderFileTile = (f) => {
    const isSelected = selected.includes(f.id);
    const thumbUrl = thumbUrls[f.id];
    const isVideo = f.mime_type?.startsWith("video/");
    const typeLabel = f.mime_type ? f.mime_type.split("/")[1]?.toUpperCase() : "";
    return (
      <button
        key={f.id}
        data-testid={`drive-file-${f.id}`}
        onClick={() => toggleSelect(f.id)}
        className={`relative aspect-square rounded-[10px] overflow-hidden border-2 transition-colors bg-[#f5f5f5] ${isSelected ? "border-[#5FA8B4]" : "border-[#e5e7eb] hover:border-[#5FA8B4]"}`}
      >
        {thumbUrl ? (
          <>
            <img src={thumbUrl} alt={f.name} className="w-full h-full object-cover" />
            {isVideo && (
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="bg-black/50 rounded-full p-1.5"><Play size={14} weight="fill" className="text-white" /></span>
              </span>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#9AA6AC]">
            {isVideo ? <VideoCamera size={24} /> : <ImageIcon size={24} />}
          </div>
        )}
        {isSelected && (
          <span className="absolute top-1 right-1 bg-[#5FA8B4] text-white rounded-full p-0.5">
            <Check size={12} weight="bold" />
          </span>
        )}
        <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-1 py-0.5 flex items-center justify-between gap-1">
          <span className="truncate">{f.name}</span>
          {typeLabel && <span className="opacity-70 shrink-0">{typeLabel}</span>}
        </span>
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-3xl" data-testid="drive-import-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>
            Import from Google Drive
          </DialogTitle>
        </DialogHeader>

        {loadingStatus ? (
          <p className="text-sm text-[#7A9090] py-6 text-center" data-testid="drive-loading">Checking connection…</p>
        ) : !status?.connected ? (
          <div className="py-6 text-center space-y-4" data-testid="drive-not-connected">
            <p className="text-sm text-[#7A9090]">Connect your personal Google Drive to browse and import images/videos straight into Pending Review.</p>
            <Button data-testid="drive-connect-button" onClick={handleConnect} disabled={connecting} className="bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] rounded-[12px] gap-2">
              <GoogleLogo size={18} weight="bold" /> {connecting ? "Connecting…" : "Connect Google Drive"}
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="drive-connected-panel">
            <div className="flex items-center justify-between">
              <p className="text-xs text-[#7A9090]" data-testid="drive-connected-email">
                Connected as <span className="font-semibold text-[#3C4048]">{status.email || "your Google account"}</span>
              </p>
              <button onClick={handleDisconnect} data-testid="drive-disconnect-button" className="text-xs text-[#ef4444] hover:underline flex items-center gap-1">
                <LinkBreak size={12} /> Disconnect
              </button>
            </div>
            <div className="flex gap-2">
              <Input
                data-testid="drive-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="Search your Drive by file name…"
                className="bg-[#fafafa] border-[#e5e7eb] text-[#3C4048]"
              />
              <Button data-testid="drive-search-button" onClick={handleSearch} className="bg-white border border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#f0f7f8] rounded-[12px]">
                <MagnifyingGlass size={16} />
              </Button>
            </div>

            {searchActive ? (
              <>
                <button data-testid="drive-clear-search" onClick={clearSearch} className="text-xs text-[#3A7A8C] hover:underline flex items-center gap-1">
                  <ArrowLeft size={12} weight="bold" /> Back to folder browsing
                </button>

                {filesLoading && files.length === 0 ? (
                  <p className="text-sm text-[#7A9090] py-8 text-center">Searching your Drive…</p>
                ) : files.length === 0 ? (
                  <p className="text-sm text-[#7A9090] py-8 text-center" data-testid="drive-empty-state">No images or videos found matching your search.</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-80 overflow-y-auto" data-testid="drive-files-grid">
                    {files.map(renderFileTile)}
                  </div>
                )}

                {nextPageToken && (
                  <button data-testid="drive-load-more" onClick={() => fetchFiles(nextPageToken, search.trim() || undefined)} className="text-xs text-[#3A7A8C] hover:underline w-full text-center">
                    {filesLoading ? "Loading…" : "Load more"}
                  </button>
                )}
              </>
            ) : (
              <>
                <div className="flex items-center gap-1 text-xs text-[#7A9090] overflow-x-auto pb-1" data-testid="drive-breadcrumbs">
                  <button
                    data-testid="drive-back-button"
                    onClick={goBack}
                    disabled={folderStack.length <= 1}
                    className={`mr-2 flex items-center gap-1 shrink-0 ${folderStack.length <= 1 ? "text-[#c7d3d3] cursor-not-allowed" : "text-[#3A7A8C] hover:underline"}`}
                  >
                    <ArrowLeft size={12} weight="bold" /> Back
                  </button>
                  {folderStack.map((f, i) => (
                    <span key={f.id} className="flex items-center gap-1 shrink-0">
                      {i > 0 && <CaretRight size={10} className="text-[#c7d3d3]" />}
                      <button
                        data-testid={`drive-breadcrumb-${i}`}
                        onClick={() => goToBreadcrumb(i)}
                        className={i === folderStack.length - 1 ? "text-[#3C4048] font-semibold" : "text-[#3A7A8C] hover:underline"}
                      >
                        {f.name}
                      </button>
                    </span>
                  ))}
                </div>

                {browseLoading && browseItems.folders.length === 0 && browseItems.files.length === 0 ? (
                  <p className="text-sm text-[#7A9090] py-8 text-center">Loading folder…</p>
                ) : browseItems.folders.length === 0 && browseItems.files.length === 0 ? (
                  <p className="text-sm text-[#7A9090] py-8 text-center" data-testid="drive-folder-empty-state">This folder is empty.</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-80 overflow-y-auto" data-testid="drive-browse-grid">
                    {browseItems.folders.map((f) => (
                      <button
                        key={f.id}
                        data-testid={`drive-folder-${f.id}`}
                        onClick={() => openFolder(f)}
                        className="relative aspect-square rounded-[10px] border-2 border-[#e5e7eb] hover:border-[#5FA8B4] bg-[#f5f5f5] flex flex-col items-center justify-center gap-1 p-2 transition-colors"
                      >
                        <Folder size={28} weight="fill" className="text-[#5FA8B4]" />
                        <span className="text-[10px] text-[#3C4048] text-center truncate w-full">{f.name}</span>
                      </button>
                    ))}
                    {browseItems.files.map(renderFileTile)}
                  </div>
                )}

                {browseNextPageToken && (
                  <button
                    data-testid="drive-browse-load-more"
                    onClick={() => fetchBrowse(folderStack[folderStack.length - 1].id, browseNextPageToken)}
                    className="text-xs text-[#3A7A8C] hover:underline w-full text-center"
                  >
                    {browseLoading ? "Loading…" : "Load more"}
                  </button>
                )}
              </>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-[12px]" data-testid="drive-cancel-button">Cancel</Button>
              <Button data-testid="drive-import-button" onClick={handleImport} disabled={importing || selected.length === 0} className="bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] rounded-[12px] gap-2">
                {importing ? "Importing…" : `Import ${selected.length || ""} file(s)`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
